<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useFareOptimizerStore } from '@/store/fareOptimizer'
import { useSuccessStore } from '@/store/success'
import { useCurrencyFormaterFromCents } from '@/composable/useCurrencyFormater'
import { toMonthKey } from '@/utils/date/date-time'
import { energyConsumerUnitSchema } from '@/schemas/EnergyConsumerUnitSchema'
import MonthYearPicker from '@/components/Globals/Inputs/MonthYearPicker.vue'
import IcLoader from '@/components/Icons/IcLoader.vue'
import ConsumerUnitSelector from '@/components/FareOptimizer/ConsumerUnitSelector.vue'

const props = defineProps<{ isOpen: boolean }>()
const emit = defineEmits<{ (e: 'close'): void }>()

const { t } = useI18n()
const store = useFareOptimizerStore()
const {
  referenceMonth,
  distribuidora,
  isSavingInvoice,
  validationAlerts,
  canSaveInvoice,
  hasBlockingAlerts,
  tariffTotalTable,
  existingInvoiceForReferenceMonth,
  requiresConsumerUnitSelection,
  consumerUnits,
} = storeToRefs(store)

const hasConsumerUnits = computed(() => consumerUnits.value.length >= 1)
const isCreatingUnit = ref(false)
const isSubmitting = ref(false)
const newUnitCode = ref('')
const newUnitName = ref('')
const codeError = ref('')
const nameError = ref('')

const willOverwriteComplete = computed(() => existingInvoiceForReferenceMonth.value?.complete === true)
const totalLabel = computed(() => useCurrencyFormaterFromCents(tariffTotalTable.value))
const isBusy = computed(() => isSavingInvoice.value || isSubmitting.value)

const canConfirmSave = computed(() => {
  if (isCreatingUnit.value) {
    return !hasBlockingAlerts.value && !!referenceMonth.value && !isBusy.value
  }
  return canSaveInvoice.value && !isBusy.value
})

function previousMonthKey(): string {
  const now = new Date()
  return toMonthKey(new Date(now.getFullYear(), now.getMonth() - 1, 1))
}

function clearInlineErrors() {
  codeError.value = ''
  nameError.value = ''
}

function startInlineCreate() {
  isCreatingUnit.value = true
  newUnitCode.value = ''
  newUnitName.value = ''
  clearInlineErrors()
}

function cancelInlineCreate() {
  isCreatingUnit.value = false
  newUnitCode.value = ''
  newUnitName.value = ''
  clearInlineErrors()
}

watch(
  () => props.isOpen,
  (open) => {
    if (!open) {
      cancelInlineCreate()
      return
    }
    if (!referenceMonth.value) store.setReferenceMonth(previousMonthKey())
    store.fetchInvoices()
  },
)

async function validateNewUnit(): Promise<boolean> {
  clearInlineErrors()
  try {
    await energyConsumerUnitSchema.validate(
      { code: newUnitCode.value.trim(), name: newUnitName.value.trim() },
      { abortEarly: false },
    )
    return true
  } catch (error) {
    const yupError = error as { inner?: { path?: string; message: string }[] }
    yupError.inner?.forEach((item) => {
      if (item.path === 'code') codeError.value = item.message
      if (item.path === 'name') nameError.value = item.message
    })
    return false
  }
}

async function confirm() {
  if (isCreatingUnit.value) {
    const valid = await validateNewUnit()
    if (!valid) return

    isSubmitting.value = true
    try {
      const created = await store.createConsumerUnit({
        code: newUnitCode.value.trim(),
        name: newUnitName.value.trim(),
        active: true,
      })
      if (!created) return

      const ok = await store.saveInvoice()
      if (!ok) return

      useSuccessStore().setSuccess([t('contract_optimizer.invoice.saved_with_unit_success')])
      cancelInlineCreate()
      emit('close')
    } finally {
      isSubmitting.value = false
    }
    return
  }

  const ok = await store.saveInvoice()
  if (!ok) return
  useSuccessStore().setSuccess([t('contract_optimizer.invoice.saved_success')])
  emit('close')
}
</script>

<template>
  <div v-if="isOpen" class="fixed inset-0 z-[60] flex items-center justify-center p-4">
    <div class="absolute inset-0 bg-black/40" @click="emit('close')" />
    <div class="relative flex w-full max-w-lg flex-col gap-5 rounded-none bg-white p-6 shadow-xl">
      <h3 class="text-lg font-semibold text-slate-900">{{ t('contract_optimizer.invoice.save_dialog_title') }}</h3>

      <div v-if="hasConsumerUnits" class="relative z-30 flex flex-col gap-5">
        <div class="rounded-none">
          <ConsumerUnitSelector
            :creating="isCreatingUnit"
            @create="startInlineCreate"
            @unit-selected="cancelInlineCreate" />
          <p v-if="requiresConsumerUnitSelection && !isCreatingUnit" class="mt-2 text-sm text-red">
            {{ t('contract_optimizer.consumer_units.select_required') }}
          </p>
        </div>

        <div v-if="isCreatingUnit" class="grid grid-cols-2 gap-4">
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-slate-700">
              {{ t('contract_optimizer.consumer_units.fields.code') }}
            </label>
            <input
              v-model="newUnitCode"
              type="text"
              class="h-11 w-full rounded-none border-2 border-stroke px-4 transition-colors hover:border-blue focus:border-blue focus:outline-none"
              @input="codeError = ''" />
            <p v-if="codeError" class="text-sm text-red">{{ codeError }}</p>
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-slate-700">
              {{ t('contract_optimizer.consumer_units.fields.name') }}
            </label>
            <input
              v-model="newUnitName"
              type="text"
              class="h-11 w-full rounded-none border-2 border-stroke px-4 transition-colors hover:border-blue focus:border-blue focus:outline-none"
              @input="nameError = ''" />
            <p v-if="nameError" class="text-sm text-red">{{ nameError }}</p>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-slate-700">{{ t('contract_optimizer.invoice.reference_month') }}</label>
        <MonthYearPicker :model-value="referenceMonth" @update:model-value="store.setReferenceMonth($event)" />
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-slate-700">{{ t('contract_optimizer.invoice.distribuidora') }}</label>
        <input
          :value="distribuidora"
          class="h-11 w-full rounded-none border-2 border-stroke px-4 transition-colors hover:border-blue focus:border-blue focus:outline-none"
          @input="store.setInvoiceMetadata({ distribuidora: ($event.target as HTMLInputElement).value })" />
      </div>

      <div class="flex items-center justify-between rounded-none border border-stroke bg-slate-50 px-4 py-3">
        <span class="text-sm font-medium text-slate-600">{{ t('contract_optimizer.invoice.total_in_invoice') }}</span>
        <span class="text-base font-bold text-slate-900">{{ totalLabel }}</span>
      </div>

      <div v-if="hasBlockingAlerts" class="rounded-none bg-red-400 p-4">
        <p class="mb-2 font-satoshi text-sm font-bold text-white">{{ t('contract_optimizer.invoice.save_blocked_alerts') }}</p>
        <ul class="list-disc pl-5">
          <li v-for="(alert, index) in validationAlerts" :key="index" class="mb-1 font-satoshi text-xs text-white">{{ alert }}</li>
        </ul>
      </div>

      <p v-else-if="willOverwriteComplete" class="rounded-none bg-amber-100 px-4 py-3 text-sm text-amber-800">
        {{ t('contract_optimizer.invoice.overwrite_warning') }}
      </p>

      <div class="flex justify-end gap-3 border-t border-stroke pt-4">
        <button
          type="button"
          class="rounded-none border border-stroke px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          @click="emit('close')">
          {{ t('contract_optimizer.invoice.cancel') }}
        </button>
        <button
          type="button"
          :disabled="!canConfirmSave"
          class="inline-flex items-center gap-2 rounded-none bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          @click="confirm">
          <IcLoader v-if="isBusy" class="h-4 w-4 animate-spin" />
          <template v-if="isCreatingUnit">
            {{ t('contract_optimizer.consumer_units.create_and_save') }}
          </template>
          <template v-else>
            {{ isBusy ? t('contract_optimizer.invoice.saving') : t('contract_optimizer.invoice.confirm_save') }}
          </template>
        </button>
      </div>
    </div>
  </div>
</template>
